/* import __COLOCATED_TEMPLATE__ from './composer.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
import { BaseConfig, BlocksDocument, type ComposerPublicAPI } from '@intercom/composer-ember';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import { type Paragraph } from '@intercom/interblocks.ts';
import { EMOJI_TYPEAHEAD } from '@intercom/composer-ember';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import contentTypeaheadConfig from './content-typeahead-config';
import { hasFeature } from 'embercom/helpers/has-feature';
import {
  MAX_SERIALIZED_HTML_LENGTH,
  getVisibleCharacterCount,
} from 'embercom/lib/ai-agent/guidance-utils';

const CONTENT_TYPEAHEAD = contentTypeaheadConfig;
const FEATURE_FLAG_HTML_LENGTH_VALIDATION = 'ai-agent-guidance-html-length-validation';

class ComposerConfig extends BaseConfig {
  autoFocus = true;
  allowedBlocks = ['paragraph'];
  allowedInline = ['anchor'];
  allowedMarks = ['anchor'];
  maxCharacterCount = 2500;
  placeholder: string;
  tools: Array<{ name: string }>;
  templating?: { picker: string; resolver: AttributeInfoResolver };
  experimental = {
    hideInsertersOnMouseOut: true,
  };
  typeaheads = [EMOJI_TYPEAHEAD];
  guideline: Guideline;

  constructor({
    resolver,
    placeholder,
    guideline,
    appService,
    isCopilot = false,
  }: {
    resolver: AttributeInfoResolver;
    placeholder: string;
    guideline: Guideline;
    appService?: any;
    isCopilot?: boolean;
  }) {
    super();
    this.placeholder = placeholder;
    this.guideline = guideline;

    // Only include content typeahead for 'content' category guidelines and if feature flag is enabled
    if (guideline.category === 'content' && hasFeature('ai-agent-content-guidance', appService)) {
      this.typeaheads.push(CONTENT_TYPEAHEAD);
    }

    if (isCopilot) {
      this.tools = [];
    } else {
      // For other categories, allow inserting attributes
      this.tools = [{ name: 'template-inserter' }, { name: 'fallback-editor' }];
      this.templating = { picker: 'common/attribute-picker', resolver };
    }
  }
}

interface Signature {
  Element: HTMLDivElement;
  Args: {
    guideline: Guideline;
    placeholder: string;
    onReady: (composer: ComposerPublicAPI) => void;
    onChange?: () => void;
    registerRefreshHook: (hook: () => void) => void;
    unregisterRefreshHook: (hook: () => void) => void;
    applyTextGradientShimmerAnimation?: boolean;
  };
  Blocks: {
    default: [ComposerPublicAPI];
  };
}

export default class Composer extends Component<Signature> {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare attributeService: $TSFixMe;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;

  @tracked blocksDoc: BlocksDocument;
  @tracked composer: ComposerPublicAPI | null = null;

  constructor(owner: unknown, args: never) {
    super(owner, args);
    this.blocksDoc = new BlocksDocument(
      this.args.guideline.prompt
        ?.split('\n')
        .map((line) => ({ type: 'paragraph', text: line, class: 'no-margin' })) || [],
    );

    this.args.registerRefreshHook(this.setupComposer);
  }

  @action
  setupComposer() {
    this.blocksDoc = new BlocksDocument(
      this.args.guideline.prompt
        ?.split('\n')
        .map((line) => ({ type: 'paragraph', text: line, class: 'no-margin' })) || [],
    );
  }

  willDestroy() {
    super.willDestroy();
    this.args.unregisterRefreshHook(this.setupComposer);
  }

  get app() {
    return this.appService.app;
  }

  get composerConfig() {
    let config = new ComposerConfig({
      resolver: this.aiAgentGuidelineService.attributeInfoResolver(),
      placeholder: this.args.placeholder,
      guideline: this.args.guideline,
      appService: this.appService,
      isCopilot: this.args.guideline.category === 'copilot',
    });
    return config;
  }

  get isHtmlLengthValidationEnabled(): boolean {
    return hasFeature(FEATURE_FLAG_HTML_LENGTH_VALIDATION, this.appService);
  }

  get serializedHtmlLength(): number {
    return this.args.guideline.prompt?.length ?? 0;
  }

  get visibleCharacterCount(): number {
    return getVisibleCharacterCount(this.args.guideline.prompt);
  }

  get currentCharacterCount(): number {
    // When FF enabled, use visible character count; otherwise use raw prompt length
    return this.isHtmlLengthValidationEnabled
      ? this.visibleCharacterCount
      : this.serializedHtmlLength;
  }

  get remainingCharacters(): number {
    return this.composerConfig.maxCharacterCount - this.currentCharacterCount;
  }

  get isOverSerializedLimit(): boolean {
    return this.serializedHtmlLength > MAX_SERIALIZED_HTML_LENGTH;
  }

  get charactersOverSerializedLimit(): number {
    return this.serializedHtmlLength - MAX_SERIALIZED_HTML_LENGTH;
  }

  get shouldShowSerializedWarning(): boolean {
    // Only show serialized warning when FF is enabled
    return (
      this.isHtmlLengthValidationEnabled &&
      this.visibleCharacterCount <= this.composerConfig.maxCharacterCount &&
      this.isOverSerializedLimit
    );
  }

  get shouldShowRemainingCharacters(): boolean {
    return this.currentCharacterCount > this.composerConfig.maxCharacterCount * 0.8;
  }

  @action
  onChange(blocksDoc: BlocksDocument) {
    this.args.guideline.prompt = blocksDoc.blocks
      .map((block: Paragraph) => block.text)
      .join('\n')
      .trim();
    this.args.onChange?.();
  }

  @action
  onReady(composer: ComposerPublicAPI) {
    // this.composer = composer;
    this.args.onReady(composer);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::Composer': typeof Composer;
    'ai-agent/guidance/composer': typeof Composer;
  }
}
